import * as React from 'react';

import Options from '@/components/Tables/Cells/ListCell/options';
import {
  valueNormalize as defaultValueNormalize,
  filterList as defaultFilterList,
} from '@/components/Tables/Cells/ListCell/helpers';
import { keyCodeArrow } from '@/components/Tables/helpers';
import { executeFunc } from '@/lib/helpers';

import elements from '@/components/elements';
import AutoTextArea from '@/components/Tables/Cells/ListCell/AutoTextArea';
import { ListCellWrapper, FieldWrapper, FieldInput } from '@/components/Tables/Cells/ListCell/StyledComponents';
import { CellToggleButton } from '@/components/Tables/Cells/Cell/StyledComponents';

import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';

const ONE_SYMBOL_WIDTH = 7.5;

class HintInput extends React.Component {
  static defaultProps = {
    valueNormalize: defaultValueNormalize,
    filterList: defaultFilterList,
    blurTimeOut: 100,
  };

  static defIndex() {
    // this would normally return first selected element
    // currently we dont select first option to allow partially entered values
    return -1;
  }

  state = {
    show: true,
    index: -1,
    isDirectionUP: undefined,
    style: {},
  };

  componentDidUpdate(prevProps) {
    if (this.props.input.value && this.props.input.value !== prevProps.input.value) {
      this.setActive(-1);
    }
  }

  onBlur = () => {
    this.timeout = setTimeout(() => {
      this.handleOpen(false, this.props.input.onBlur);
    }, this.props.blurTimeOut);
    this.focused = false;
  };

  onFocus = () => {
    this.focused = true;
    if (this.timeout) {
      clearTimeout(this.timeout);
      this.timeout = undefined;
    }
  };

  get value() {
    return this.props.input.value.value || '';
  }

  get optionList() {
    const list = this.getOptionList(this.props.input.value.value);
    if (this.state.isDirectionUP) {
      return list.reverse();
    }
    return list;
  }

  get isOpen() {
    const { show } = this.state;
    const { length } = this.value;
    const maxLengthOptions = 8;
    const options = this.optionList;
    return Boolean(length > 1 && maxLengthOptions >= options.size && show);
  }

  get position() {
    const wh = () => window.innerHeight;
    if (this.input && window) {
      const { bottom } = this.input.getBoundingClientRect();
      const maxHeightBottom = wh() - bottom;
      return maxHeightBottom;
    }
    return wh();
  }

  get index() {
    const { index } = this.state;
    if (typeof index === 'number' && this.optionList.size > 0 && this.optionList.get(index)) return index;
    return this.constructor.defIndex();
  }

  setActive = (index) => this.setState({ index });

  getOptionList = (term) => {
    const { optionList, filterList } = this.props;
    return filterList(optionList, term || '');
  };

  getMinWidth = () => {
    const optionList = this.getOptionList();
    const widthByData = optionList.reduce((res, item) => {
      const currentWidth = item.display.length * ONE_SYMBOL_WIDTH;
      return Math.max(res, currentWidth);
    }, 0);
    return this.input ? Math.max(this.input.offsetWidth, widthByData) : widthByData;
  };

  setDirection = (isUp) => {
    this.setState({
      isDirectionUP: isUp,
      index: this.constructor.defIndex(),
    });
  };

  doSelect = (index, cb) => {
    if (index !== -1) {
      const item = this.optionList.get(index);
      if (item) {
        this.props.input.onChange({ target: { value: item.id } }, cb);
      }
    } else {
      this.props.input.onKeyDown({ keyCode: 13 });
    }
  };

  keyDown = (e) => {
    const { index } = this.state;
    const currentIndex = index === null ? -1 : index;

    if (e.keyCode === 9) {
      e.preventDefault();
      this.props.input.onKeyDown({ keyCode: 13 });
    }

    if (keyCodeArrow.has(e.keyCode)) {
      this.props.input.onKeyDown({ keyCode: e.keyCode });
    }
    if (e.keyCode === 38) {
      this.setActive(((currentIndex || this.optionList.size) - 1) % this.optionList.size);
    }
    if (e.keyCode === 40) {
      this.setActive((currentIndex + 1) % this.optionList.size);
    }
    if (e.keyCode === 27) {
      this.props.input.onKeyDown({ keyCode: 27 });
    }
    if (e.keyCode === 13) {
      // use dropdown selected value only if we have dropdown visible,
      // e.g 2 or more symbols are entered
      if (this.props.input.value.value && this.props.input.value.value.length > 1) {
        this.doSelect(this.index, () => this.props.input.onKeyDown({ keyCode: 13 }));
      } else {
        this.props.input.onKeyDown({ keyCode: 13 });
      }
    }
    if (e.keyCode === 8) {
      if (this.slected) {
        this.props.input.onChange({ target: { value: '' } });
        this.slected = false;
        this.handleOpen(true);
      }
    }
  };

  refsProxy = (el) => {
    this.input = el;
    this.initOptionListPosition();
    this.props.getRefs(el);
  };

  handleOpen = (state, cb) => {
    this.setState(
      {
        show: typeof state === 'undefined' ? !this.state.show : state,
      },
      () => {
        executeFunc(cb);
      },
    );
  };

  initOptionListPosition = () => {
    if (this.input) {
      const { left } = this.input.getBoundingClientRect();
      const width = this.getMinWidth();
      const overlap = width + left + 5 - window.innerWidth;
      const currentLeft = overlap > 0 ? left - overlap : left;
      const style = { left: currentLeft };

      this.setState({ style });
    }
  };

  slected;

  input;

  wrapper;

  focused;

  timeout;

  listRef = React.createRef();

  render() {
    const { name, maxLength, style, id, placeholder, disabled, input, type } = this.props;

    const inputProps = {
      style,
      id,
      'aria-autocomplete': 'list',
      autoComplete: 'no',
      placeholder,
      name: name._cell,
      value: this.value,
      disabled,
      onKeyDown: this.keyDown,
      onChange: input.onChange,
      ...(!['float', 'number'].includes(type) && { maxLength }),
      onDoubleClick: () => this.handleOpen(true),
      'data-element': elements.je.hintinput.field,
      'data-element-id': `${name._cell}-${name.name}`,
    };

    return (
      <ListCellWrapper
        tabIndex="0" // eslint-disable-line jsx-a11y/no-noninteractive-tabindex
        onFocus={this.onFocus}
        onBlur={this.onBlur}
        role="combobox"
        aria-expanded={this.isOpen}
        aria-controls={id}
        data-element={elements.je.hintinput.container}
        data-element-id={name.name}
      >
        <FieldWrapper>
          {type === 'string_auto_generated' ? (
            <AutoTextArea {...inputProps} refsProxy={this.refsProxy} />
          ) : (
            <FieldInput {...inputProps} ref={this.refsProxy} />
          )}
        </FieldWrapper>
        {this.isOpen ? (
          <Options
            inputEl={this.input}
            minWidth={this.getMinWidth()}
            maxWidth={390}
            rtl={this.props.rtl}
            optionList={this.optionList}
            doSelect={(e) => this.doSelect(e, this.props.onComplete)}
            valueNormalize={this.props.valueNormalize}
            index={this.state.index}
            term={this.value}
            setDirection={this.setDirection}
            pageItemsCount={10}
            itemHeight={30}
            setListRef={this.listRef}
            addNewItem={null}
            setActive={this.setActive}
            style={this.state.style}
            isDisabledScroll={type === 'string_auto_generated'}
          />
        ) : null}

        {this.optionList.size && this.value.length > 1 ? (
          <CellToggleButton onClick={() => this.handleOpen()} data-element={elements.je.autocomplete.shield}>
            {this.isOpen ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />}
          </CellToggleButton>
        ) : null}
      </ListCellWrapper>
    );
  }
}

HintInput.displayName = 'HintInput';

export { HintInput as Hint };

export default HintInput;
